import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Video, Ban, Lock, CalendarCheck, Clock, UserCheck, type LucideIcon } from "lucide-react";
import AmbassadorShell, { card, eyebrow, ghostBtn, gold, h1Style, ink, line, muted, primaryBtn, Reveal } from "./_AmbassadorShell";

export default function AmbassadorPlaybook() {
  return (
    <AmbassadorShell>
      <Helmet>
        <title>The Circle Playbook — How to Run a Circle | Eva Insiders</title>
        <meta name="description" content="A free operator's guide to running a Circle: five to fifteen small-business owners meeting to learn AI together. No upsell, no mastermind pitch." />
        <link rel="canonical" href="https://hireeva.co/ambassadors/playbook" />
        <meta property="og:title" content="The Circle Playbook — Eva Insiders" />
        <meta property="og:description" content="A free operator's guide to running a Circle of small-business owners learning AI together." />
        <meta property="og:url" content="https://hireeva.co/ambassadors/playbook" />
        <meta property="og:type" content="article" />
      </Helmet>
      <style>{`
        @media (max-width: 640px) {
          .eva-amb-hero-cta { width: 100%; flex-direction: column; align-items: stretch; }
          .eva-amb-hero-cta > a { width: 100%; text-align: center; justify-content: center; }
        }
      `}</style>
      {/* HERO */}
      <Reveal as="section">
        <Link to="/ambassadors" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: muted, textDecoration: "none", fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 18 }}>
          <span aria-hidden>←</span> Eva Insiders
        </Link>
        <p style={eyebrow}>
          <span aria-hidden style={{ display: "inline-block", width: 6, height: 6, borderRadius: 999, background: "#C4823A", boxShadow: "0 0 8px #C4823A", marginRight: 8, verticalAlign: "middle" }} />
          The Circle Playbook
        </p>
        <h1 style={h1Style}>How to run <em>a Circle.</em></h1>
        <p style={{ fontSize: 18, color: muted, maxWidth: 680, marginBottom: 14, fontStyle: "italic", lineHeight: 1.55 }}>
          A free operator's guide. No upsell, no $10K mastermind pitch.
        </p>
        <p style={{ fontSize: 16, color: muted, maxWidth: 680, marginBottom: 20, lineHeight: 1.7 }}>
          A Circle is five to fifteen small-business owners who meet on a recurring video call to learn AI together, share what's working, and hold each other accountable. This page tells you exactly how to start one and run it. Steal whatever's useful.
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 28 }}>
          {["5–15 owners", "60 minutes", "Weekly or biweekly"].map((c) => (
            <span key={c} style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 12px", borderRadius: 999,
              border: `1px solid rgba(196,130,58,0.35)`,
              background: "rgba(196,130,58,0.07)",
              fontSize: 12, color: "#8a5a22", fontWeight: 600, letterSpacing: "0.02em",
            }}>
              <span aria-hidden style={{ width: 5, height: 5, borderRadius: 999, background: "#C4823A" }} />
              {c}
            </span>
          ))}
        </div>
        <div className="eva-amb-hero-cta" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Link to="/ambassadors/apply" style={primaryBtn}>Apply to be an Insider →</Link>
          <Link to="/ambassadors" style={ghostBtn}>Back to the program</Link>
        </div>
      </Reveal>

      {/* WHY */}
      <Section title="Why a Circle (not a mastermind funnel)" eyebrow="The thesis">
        <div style={{ ...card, background: "#FBF8F1", borderColor: line }}>
          <p style={{ margin: "0 0 12px", fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontSize: 22, lineHeight: 1.4, color: ink }}>
            The big AI labs are building for the Fortune 500. You need each other to keep up.
          </p>
          <p style={{ margin: 0, color: muted, fontSize: 16, lineHeight: 1.7 }}>
            We're not selling you a high-ticket mastermind. We're telling you to invite five to fifteen owners you respect, get on a call every week or two, and figure this out together. Eva is a core part of how you learn — but the room is the point. Comradery. People who actually understand what running a 10–50 person business with payroll and customers and a P&L feels like.
          </p>
        </div>
      </Section>

      {/* WHAT IT IS */}
      <Section title="What a Circle actually is" eyebrow="Plain definition">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
          <Fact label="Size" value="5–15 owners" detail="Small enough that everyone speaks. Large enough for real diversity." />
          <Fact label="Cadence" value="Weekly or biweekly" detail="Same day, same time. 60-minute hard stop." />
          <Fact label="Format" value="Video call" detail="Cameras on. Google Meet or Zoom. That's it." />
          <Fact label="Half AI, half business" value="Both" detail="What you tried with Eva this week + what's actually broken in your business." />
        </div>
      </Section>

      {/* SETUP */}
      <Section title="The 4-step setup" eyebrow="Run your first one in two weeks">
        <div style={{ display: "grid", gap: 14 }}>
          <Step n="01" h="Pick your people">
            Aim for owners running businesses with roughly 10–50 employees. Complementary, not competitive (no two owners in the same niche fighting for the same customer). People who'll actually show up — energy and reliability beat résumé every time. Start with five committed owners; add as you go.
          </Step>
          <Step n="02" h="Set the cadence">
            Pick weekly or biweekly. Pick the day and the time. Lock it for 90 days, then reassess. Calendar invites with a stable video link. 60 minutes, hard stop. People will respect a meeting that ends on time.
          </Step>
          <Step n="03" h="Pick a format">
            Choose one of the three templates below for your first month. Run the same one three times before changing anything — give the format a chance to work before you tweak it.
          </Step>
          <Step n="04" h="Run the first one">
            Five-minute intro round (name, business, one thing you're trying to figure out with AI right now). Then your chosen format. End with one ask each: what does each person need help with before next call. Done in 60.
          </Step>
        </div>
      </Section>

      {/* TEMPLATES */}
      <Section title="Three Circle templates" eyebrow="Pick one and run it">
        <div style={{
          padding: 24,
          borderRadius: 16,
          background: "linear-gradient(135deg, rgba(196,130,58,0.08) 0%, rgba(196,130,58,0.02) 60%, transparent 100%)",
          border: `1px solid rgba(196,130,58,0.18)`,
        }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
          <Template
            name="The Workshop"
            best="Best for: groups that want to learn AI fast"
            schedule={[
              "20 min · one member demos something they built or tried",
              "20 min · group Q&A and reactions",
              "20 min · open floor — what's everyone else working on",
            ]}
            note="Rotate the demo slot weekly. The forcing function of having to demo makes everyone actually try things."
          />
          <Template
            name="The Hot Seat"
            best="Best for: groups going through real strategic decisions"
            schedule={[
              "5 min · the hot-seat owner sets the problem",
              "40 min · group questions, reactions, ideas",
              "15 min · open floor + commitments for next call",
            ]}
            note="One owner per week is in the chair, with their hardest current business problem. Rotates. The chair leaves with three things to try."
            featured
          />
          <Template
            name="The Stand-up"
            best="Best for: groups with 8+ members"
            schedule={[
              "5 min each · round robin",
              "Each owner shares: one win, one stuck, one ask",
              "Last 10 min · group connects asks to people who can help",
            ]}
            note="Tight, fast, unglamorous. Works because nobody hides. Best when the group already knows each other."
          />
        </div>
        </div>
      </Section>

      {/* RESEARCH-BACKED BONES */}
      <Section title="The research-backed bones" eyebrow="What makes peer groups actually work">
        <p style={{ color: muted, fontSize: 15, lineHeight: 1.7, margin: "0 0 18px", maxWidth: 720 }}>
          Decades of research on peer-learning groups, executive forums, and YPO/EO-style chapters point to the same handful of variables. None of this is fancy. All of it matters.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
          {[
            { lead: "Consistency", body: "Same time, same day, same place. Cancellations corrode the room faster than anything else." },
            { lead: "Vulnerability norm", body: "The first owner to share something hard sets the ceiling for the whole group. Be that owner in your first three calls." },
            { lead: "Attendance commitment", body: "Aim for >75% attendance. Below that, the room loses momentum and trust." },
            { lead: "Small enough to speak", body: "Past 15, people stay quiet. Past 20, the format breaks." },
            { lead: "Large enough for diversity", body: "Below 5, one absence kills the call. Aim for 8–12 active owners." },
            { lead: "No pitching inside the room", body: "The moment someone tries to sell, trust evaporates. Hold the line." },
          ].map((b) => (
            <div key={b.lead} style={{ ...card, padding: 16, background: "#FBF8F1" }}>
              <div style={{ fontSize: 14, color: ink, fontWeight: 700, marginBottom: 4 }}>{b.lead}</div>
              <p style={{ margin: 0, color: muted, fontSize: 13, lineHeight: 1.6 }}>{b.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* HOUSE RULES */}
      <Section title="The founder's house rules" eyebrow="Tips & ground rules">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 }}>
          <Rule icon={Video} h="Cameras on">Or it's a podcast, not a Circle.</Rule>
          <Rule icon={Ban} h="No pitching">Inside the room, nobody sells anybody anything. Take it to DM after.</Rule>
          <Rule icon={Lock} h="Chatham House on specifics">Ideas leave the room. Numbers and names don't.</Rule>
          <Rule icon={CalendarCheck} h="Show up or cycle out">Below 75% attendance over a quarter, you give up the seat to someone who'll use it.</Rule>
          <Rule icon={Clock} h="End on time">Always. People will trust the format if you protect the clock.</Rule>
          <Rule icon={UserCheck} h="One operator runs it">You. Not a vote, not a co-host. One person owns the agenda and the start time.</Rule>
        </div>
      </Section>

      {/* TOOLS */}
      <Section title="Tools we recommend" eyebrow="We don't sell any of these">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
          <Tool h="Video">Google Meet or Zoom.</Tool>
          <Tool h="Agenda">Shared Notion page or Google Doc.</Tool>
          <Tool h="Between-call chat">Slack, WhatsApp, or Signal.</Tool>
          <Tool h="AI playground">Eva, plus whatever the room is testing.</Tool>
        </div>
      </Section>

      {/* WHAT THE FOUNDER BRINGS */}
      <Section title="What the founder brings to your room" eyebrow="At the tier milestones">
        <p style={{ color: muted, fontSize: 14, margin: "-6px 0 18px", fontStyle: "italic", maxWidth: 680 }}>
          Optional. The Circle works without me — these drop-ins are a thank-you for building yours.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
          <Tier badge="5+" title="Monthly · 30 min with the founder">
            Live Q&A on what's moving in AI right now, your group's current business problems, and where Eva is heading. No scripted demo. You run the room; the founder shows up to it.
          </Tier>
          <Tier badge="25+" title="Weekly · 1 hour with the founder" featured>
            Same shape, more time, more depth. By this point the founder knows your room — and your room is one of the most important sources of feedback shaping where Eva goes next.
          </Tier>
        </div>
        <p style={{ color: muted, fontSize: 13, marginTop: 14, fontStyle: "italic" }}>
          Minimum 5 RSVPs for the founder to attend a scheduled call. Below that, you keep the room on your own — which is the point anyway.
        </p>
      </Section>

      {/* CTA */}
      <section style={{ marginTop: 80, padding: "48px 32px", background: ink, color: "#F8F4EC", borderRadius: 16, textAlign: "center" }}>
        <p style={{ ...eyebrow, color: "rgba(196,130,58,0.9)", marginBottom: 14 }}>Now go build the room</p>
        <p style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontSize: 20, color: "rgba(248,244,236,0.78)", margin: "0 0 14px" }}>
          Don't ask. Don't pre-announce. Just send the invite.
        </p>
        <h2 style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontSize: "clamp(32px, 4.5vw, 48px)", margin: "0 0 12px" }}>
          Pick five owners. Send the invite this week.
        </h2>
        <p style={{ color: "rgba(248,244,236,0.82)", marginBottom: 22, maxWidth: 580, marginLeft: "auto", marginRight: "auto", fontSize: 16, lineHeight: 1.6 }}>
          You don't need permission. You don't need a curriculum. You need five owners and a recurring calendar invite.
        </p>
        <div style={{ display: "inline-flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
          <Link to="/ambassadors/apply" style={{ ...primaryBtn, background: gold, color: ink }}>Apply to be an Insider →</Link>
          <Link to="/ambassadors" style={{ ...ghostBtn, color: "#F8F4EC", borderColor: "rgba(248,244,236,0.4)" }}>Back to the program</Link>
        </div>
      </section>
    </AmbassadorShell>
  );
}

function Section({ title, eyebrow: e, children }: { title: string; eyebrow: string; children: React.ReactNode }) {
  return (
    <Reveal as="section">
      <section style={{ marginTop: 72 }}>
        <p style={eyebrow}>
          <span aria-hidden style={{ display: "inline-block", width: 6, height: 6, borderRadius: 999, background: "#C4823A", boxShadow: "0 0 8px #C4823A", marginRight: 8, verticalAlign: "middle" }} />
          {e}
        </p>
        <h2 style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontSize: "clamp(30px, 4vw, 48px)", lineHeight: 1.05, margin: "0 0 22px", fontWeight: 400 }}>{title}</h2>
        {children}
      </section>
    </Reveal>
  );
}

function Step({ n, h, children }: { n: string; h: string; children: React.ReactNode }) {
  return (
    <div className="eva-amb-card-hover" style={{ ...card, display: "grid", gridTemplateColumns: "auto 1fr", gap: 18, alignItems: "start" }}>
      <div style={{ ...eyebrow, color: muted, marginBottom: 0, paddingTop: 4 }}>{n}</div>
      <div>
        <h3 style={{ fontSize: 22, margin: "0 0 8px", fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontWeight: 400 }}>{h}</h3>
        <p style={{ color: muted, margin: 0, lineHeight: 1.7 }}>{children}</p>
      </div>
    </div>
  );
}

function Template({ name, best, schedule, note, featured }: { name: string; best: string; schedule: string[]; note: string; featured?: boolean }) {
  return (
    <div className="eva-amb-card-hover" style={{ ...card, borderColor: featured ? ink : line, position: "relative" }}>
      {featured && (
        <div style={{ position: "absolute", top: -10, left: 16, background: ink, color: "#F8F4EC", padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em" }}>RECOMMENDED</div>
      )}
      <h3 style={{ fontSize: 22, margin: "0 0 6px", fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontWeight: 400 }}>{name}</h3>
      <p style={{ ...eyebrow, color: muted, margin: "0 0 14px" }}>{best}</p>
      <ul style={{ margin: "0 0 14px", paddingLeft: 18, color: muted, fontSize: 14, lineHeight: 1.7 }}>
        {schedule.map((s) => <li key={s}>{s}</li>)}
      </ul>
      <p style={{ margin: 0, color: ink, fontSize: 13, fontStyle: "italic", lineHeight: 1.55, paddingTop: 12, borderTop: `1px solid ${line}` }}>{note}</p>
    </div>
  );
}

function Rule({ icon: Icon, h, children }: { icon?: LucideIcon; h: string; children: React.ReactNode }) {
  return (
    <div style={{ ...card, padding: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        {Icon && (
          <span aria-hidden style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 28, height: 28, borderRadius: 8,
            background: "rgba(196,130,58,0.10)", color: "#8a5a22",
            flexShrink: 0,
          }}>
            <Icon size={15} strokeWidth={2} />
          </span>
        )}
        <h4 style={{ margin: 0, fontSize: 15, color: ink, fontWeight: 700 }}>{h}</h4>
      </div>
      <p style={{ margin: 0, color: muted, fontSize: 14, lineHeight: 1.6 }}>{children}</p>
    </div>
  );
}

function Tool({ h, children }: { h: string; children: React.ReactNode }) {
  return (
    <div style={{ ...card, padding: 18, background: "#FBF8F1" }}>
      <h4 style={{ margin: "0 0 6px", fontSize: 14, color: ink, fontWeight: 700, letterSpacing: "0.02em" }}>{h}</h4>
      <p style={{ margin: 0, color: muted, fontSize: 14, lineHeight: 1.6 }}>{children}</p>
    </div>
  );
}

function Fact({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div style={{ ...card }}>
      <div style={{ ...eyebrow, color: muted, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontFamily: "'Instrument Serif', serif", fontStyle: "italic", lineHeight: 1.1, marginBottom: 6, color: ink }}>{value}</div>
      <p style={{ margin: 0, color: muted, fontSize: 13, lineHeight: 1.55 }}>{detail}</p>
    </div>
  );
}

function Tier({ badge, title, children, featured }: { badge: string; title: string; children: React.ReactNode; featured?: boolean }) {
  return (
    <div style={{ ...card, borderColor: featured ? ink : line }}>
      <div style={{ display: "inline-block", background: featured ? ink : gold, color: "#F8F4EC", padding: "4px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", marginBottom: 10 }}>{badge} REFERRALS</div>
      <h3 style={{ fontSize: 20, margin: "0 0 8px", fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontWeight: 400 }}>{title}</h3>
      <p style={{ color: muted, margin: 0, fontSize: 14, lineHeight: 1.65 }}>{children}</p>
    </div>
  );
}